// Parse Turkish free-form tour dates like:
//   "18 - 22 Mart 2025"
//   "5 Nisan 2026 - 10 Nisan 2026"
//   "12-15 Mayıs 2026"
//
// Returns { startDate: Date, endDate: Date|null } or null when unparseable.

const TR_MONTHS = {
  ocak: 0, subat: 1, şubat: 1, mart: 2, nisan: 3,
  mayis: 4, mayıs: 4, haziran: 5, temmuz: 6,
  agustos: 7, ağustos: 7, eylul: 8, eylül: 8,
  ekim: 9, kasim: 10, kasım: 10, aralik: 11, aralık: 11,
};

const normalizeMonth = (m = '') =>
  m
    .toLowerCase()
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ş/g, 's')
    .replace(/ü/g, 'u');

// Extract every "day month year" triple present, in order.
// Handles cases where each side of the range has its own month/year, or where
// only the second side has month/year (day1 uses same month/year as day2),
// or cross-month range with single trailing year ("29 Ekim - 1 Kasım 2025").
export const parseTurkishDateRange = (raw = '') => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().replace(/[.,]/g, ' ');

  // Pattern A: cross-month range with single trailing year — "29 Ekim - 1 Kasım 2025"
  const crossRe = /(\d{1,2})\s*([a-zçğıöşü]+)\s*[-–]\s*(\d{1,2})\s*([a-zçğıöşü]+)\s*(\d{4})/;
  const cm = s.match(crossRe);
  if (cm) {
    const day1 = parseInt(cm[1], 10);
    const month1Key = normalizeMonth(cm[2]);
    const month1 = TR_MONTHS[month1Key];
    const day2 = parseInt(cm[3], 10);
    const month2Key = normalizeMonth(cm[4]);
    const month2 = TR_MONTHS[month2Key];
    const year = parseInt(cm[5], 10);
    if (month1 !== undefined && month2 !== undefined && day1 >= 1 && day1 <= 31 && day2 >= 1 && day2 <= 31) {
      // Dec→Jan rollover: start year is previous
      const startYear = month1 > month2 ? year - 1 : year;
      return {
        startDate: new Date(startYear, month1, day1),
        endDate: new Date(year, month2, day2),
      };
    }
  }

  // Pattern B: two full "d month year" occurrences — each side has own year
  const fullRe = /(\d{1,2})\s*([a-zçğıöşü]+)\s*(\d{4})/g;
  const fulls = [];
  let m;
  while ((m = fullRe.exec(s)) !== null) {
    const day = parseInt(m[1], 10);
    const monthKey = normalizeMonth(m[2]);
    const month = TR_MONTHS[monthKey];
    const year = parseInt(m[3], 10);
    if (month !== undefined && day >= 1 && day <= 31) {
      fulls.push({ day, month, year });
    }
  }
  if (fulls.length >= 2) {
    return {
      startDate: new Date(fulls[0].year, fulls[0].month, fulls[0].day),
      endDate: new Date(fulls[1].year, fulls[1].month, fulls[1].day),
    };
  }

  // Pattern C: same-month range "d1 [-–] d2 month year"
  const rangeRe = /(\d{1,2})\s*[-–]\s*(\d{1,2})\s*([a-zçğıöşü]+)\s*(\d{4})/;
  const rm = s.match(rangeRe);
  if (rm) {
    const day1 = parseInt(rm[1], 10);
    const day2 = parseInt(rm[2], 10);
    const monthKey = normalizeMonth(rm[3]);
    const month = TR_MONTHS[monthKey];
    const year = parseInt(rm[4], 10);
    if (month !== undefined) {
      return {
        startDate: new Date(year, month, day1),
        endDate: new Date(year, month, day2),
      };
    }
  }

  // Fallback: single "d month year"
  if (fulls.length === 1) {
    const only = fulls[0];
    return {
      startDate: new Date(only.year, only.month, only.day),
      endDate: null,
    };
  }

  return null;
};

const TR_MONTH_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const TR_MONTH_FULL = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

export const formatShortDay = (d) => (d ? d.getDate().toString() : '');
export const formatShortMonth = (d) => (d ? TR_MONTH_SHORT[d.getMonth()] || '' : '');
export const formatFullDate = (d) =>
  d ? `${d.getDate()} ${TR_MONTH_FULL[d.getMonth()]} ${d.getFullYear()}` : '';

/** Whole-day difference from today (positive = future). */
export const daysUntil = (d) => {
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};
